/**
 * PaymentsPayPalPage.js — multi-account read-only PayPal reporting.
 *
 * Admins can register any number of PayPal Developer Apps (one row per
 * real-world PayPal account) with their Client ID + Secret, verify the
 * credentials, and pull the current balance + last-30-days transaction
 * list. Sandbox first by design — flip an account to Live per-row.
 *
 * All requests hit the FastAPI backend which holds the encrypted creds
 * and proxies to `api-m.(sandbox.)paypal.com` — the browser never sees
 * a PayPal secret.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Wallet, Plus, Trash2, RefreshCw, ShieldCheck, ExternalLink, Loader2,
  ArrowDownCircle, ArrowUpCircle, CheckCircle2, XCircle, Info, Save,
  Eye, EyeOff, HelpCircle,
} from "lucide-react";

const MODE_LABEL = { sandbox: "Sandbox", live: "Live" };
const errText = (e, f = "Error") =>
  e?.response?.data?.detail || e?.message || f;

// PayPal returns ISO or "+0000" — normalise.
const fmtDate = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s.replace(" ", "T"));
    if (Number.isNaN(+d)) return s;
    return d.toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
};

const fmtMoney = (a) => {
  if (!a?.value) return "—";
  const n = Number(a.value);
  if (!Number.isFinite(n)) return `${a.currency_code || ""} ${a.value}`;
  return `${a.currency_code || ""} ${n.toLocaleString("en-AU", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

// PayPal transaction status codes → human labels.
const TX_STATUS = {
  S: "Success", P: "Pending", V: "Reversed",
  D: "Denied", F: "Partial refund",
};

export default function PaymentsPayPalPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [walkthrough, setWalkthrough] = useState(null);
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  // New / edit form state
  const [form, setForm] = useState({
    account_id: "",  // "" = new
    name: "",
    mode: "sandbox",
    client_id: "",
    client_secret: "",
    notes: "",
  });
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);

  // Reporting state (per selected account)
  const [balance, setBalance] = useState(null);
  const [balanceBusy, setBalanceBusy] = useState(false);
  const [tx, setTx] = useState(null);
  const [txBusy, setTxBusy] = useState(false);
  const [txPage, setTxPage] = useState(1);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/paypal/accounts");
      setAccounts(data.accounts || []);
      if (!selectedId && data.accounts?.[0]) {
        setSelectedId(data.accounts[0].account_id);
      }
    } catch (e) {
      toast.error(errText(e, "Failed to load PayPal accounts"));
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  const loadWalkthrough = useCallback(async () => {
    try {
      const { data } = await api.get("/paypal/walkthrough");
      setWalkthrough(data);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { loadAccounts(); loadWalkthrough(); },
    [loadAccounts, loadWalkthrough]);

  const selected = useMemo(
    () => accounts.find((a) => a.account_id === selectedId),
    [accounts, selectedId],
  );

  // ── Save / update ─────────────────────────────────────────────
  const startEdit = (acct) => {
    setForm({
      account_id: acct.account_id,
      name: acct.name || "",
      mode: acct.mode || "sandbox",
      client_id: "",  // never returned by backend — leave blank to keep
      client_secret: "",
      notes: acct.notes || "",
    });
    setShowSecret(false);
  };

  const resetForm = () => {
    setForm({
      account_id: "", name: "", mode: "sandbox",
      client_id: "", client_secret: "", notes: "",
    });
    setShowSecret(false);
  };

  const saveAccount = async () => {
    if (!form.name.trim()) { toast.error("Give the account a name"); return; }
    setSaving(true);
    try {
      if (form.account_id) {
        const body = {
          name: form.name.trim(),
          mode: form.mode,
          notes: form.notes,
        };
        if (form.client_id) body.client_id = form.client_id;
        if (form.client_secret) body.client_secret = form.client_secret;
        await api.put(`/paypal/accounts/${form.account_id}`, body);
        toast.success("Account updated");
      } else {
        if (!form.client_id || !form.client_secret) {
          toast.error("Client ID + Secret are required for a new account");
          setSaving(false); return;
        }
        const { data } = await api.post("/paypal/accounts", {
          name: form.name.trim(),
          mode: form.mode,
          client_id: form.client_id,
          client_secret: form.client_secret,
          notes: form.notes,
        });
        toast.success(`Added "${data.name}"`);
        setSelectedId(data.account_id);
      }
      resetForm();
      await loadAccounts();
    } catch (e) { toast.error(errText(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const deleteAccount = async (id) => {
    if (!window.confirm("Remove this PayPal account?")) return;
    try {
      await api.delete(`/paypal/accounts/${id}`);
      toast.success("Removed");
      if (selectedId === id) setSelectedId("");
      await loadAccounts();
    } catch (e) { toast.error(errText(e, "Delete failed")); }
  };

  const testAccount = async (id) => {
    setTestingId(id);
    try {
      const { data } = await api.post(`/paypal/accounts/${id}/test`);
      if (data.status === "ok") toast.success(`Connected — token ${data.token_preview}`);
      else toast.error(data.detail || "Connection failed");
      await loadAccounts();
    } catch (e) { toast.error(errText(e, "Test failed")); }
    finally { setTestingId(null); }
  };

  // ── Reporting ─────────────────────────────────────────────────
  const fetchBalance = async () => {
    if (!selected) return;
    setBalanceBusy(true);
    try {
      const { data } = await api.get(
        `/paypal/accounts/${selected.account_id}/balance`);
      setBalance(data);
    } catch (e) { toast.error(errText(e, "Balance fetch failed")); }
    finally { setBalanceBusy(false); }
  };

  const fetchTransactions = async (page = 1) => {
    if (!selected) return;
    setTxBusy(true);
    try {
      const { data } = await api.get(
        `/paypal/accounts/${selected.account_id}/transactions`,
        { params: { page, page_size: 50 } });
      setTx(data);
      setTxPage(page);
    } catch (e) { toast.error(errText(e, "Transactions fetch failed")); }
    finally { setTxBusy(false); }
  };

  // Wipe reporting state when the selected account changes.
  useEffect(() => { setBalance(null); setTx(null); setTxPage(1); },
    [selectedId]);

  return (
    <div className="max-w-7xl mx-auto p-4 lg:p-6 space-y-4"
         data-testid="paypal-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-overline">Legal · Finance · HR ▸ Payments</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-0.5"
              data-testid="paypal-h1">
            PayPal
          </h1>
          <p className="text-sm text-zinc-600 max-w-2xl mt-1">
            Register any number of PayPal Developer Apps below (personal,
            business, sandbox or live). Iceberg pulls a live wallet
            balance and the last 30 days of transactions from each one.
            All credentials stay encrypted server-side.
          </p>
        </div>
        <Button variant="outline" className="rounded-sm"
                onClick={() => setShowWalkthrough((v) => !v)}
                data-testid="paypal-walkthrough-toggle">
          <HelpCircle className="h-4 w-4 mr-2" />
          {showWalkthrough ? "Hide" : "Show"} setup walkthrough
        </Button>
      </div>

      {/* Walkthrough */}
      {showWalkthrough && walkthrough && (
        <Card className="rounded-sm border-amber-200 bg-amber-50"
              data-testid="paypal-walkthrough">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-amber-900">
              <Info className="h-4 w-4" />
              <div className="font-display font-bold">
                Create a PayPal Developer App
              </div>
            </div>
            <ol className="text-xs text-amber-900 space-y-2 list-none">
              {walkthrough.steps.map((s) => (
                <li key={s.n} className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-900 text-amber-50 flex items-center justify-center text-[11px] font-bold">
                    {s.n}
                  </div>
                  <div>
                    <div className="font-bold">{s.title}</div>
                    <div className="text-amber-800 mt-0.5">{s.body}</div>
                  </div>
                </li>
              ))}
            </ol>
            <div className="pt-2 flex gap-2 flex-wrap text-xs">
              <a href={walkthrough.dashboard_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-amber-900 text-amber-50 hover:bg-amber-800"
                 data-testid="paypal-dashboard-link">
                Open Developer Dashboard <ExternalLink className="h-3 w-3" />
              </a>
              <a href={walkthrough.docs_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-amber-300 text-amber-900 hover:bg-amber-100">
                REST API docs <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Accounts list */}
      <Card className="rounded-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-overline">Connected accounts</div>
              <div className="font-display font-bold text-lg mt-0.5">
                {accounts.length} account{accounts.length === 1 ? "" : "s"}
              </div>
            </div>
            {form.account_id && (
              <Button variant="ghost" size="sm" onClick={resetForm}
                      className="rounded-sm text-xs">
                Cancel edit
              </Button>
            )}
          </div>

          {loading ? (
            <div className="py-8 text-center text-xs text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
              Loading accounts…
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center text-xs text-zinc-500 py-8 border border-dashed border-zinc-200 rounded-sm">
              No PayPal accounts connected yet — fill in the form below to add
              your first one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Mode</th>
                    <th className="py-2 pr-3">Client ID</th>
                    <th className="py-2 pr-3">Last verified</th>
                    <th className="py-2 pr-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => {
                    const isSel = a.account_id === selectedId;
                    return (
                      <tr key={a.account_id}
                          className={`border-b border-zinc-100 cursor-pointer hover:bg-zinc-50 ${isSel ? "bg-blue-50" : ""}`}
                          onClick={() => setSelectedId(a.account_id)}
                          data-testid={`paypal-row-${a.account_id}`}>
                        <td className="py-2 pr-3 font-semibold">
                          {a.name}
                          {a.notes && (
                            <div className="text-[10px] text-zinc-500 font-normal mt-0.5">
                              {a.notes}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <Badge className={`rounded-sm text-[10px] ${
                            a.mode === "live"
                              ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                              : "bg-amber-100 text-amber-800 border-amber-200"}`}
                                variant="outline">
                            {MODE_LABEL[a.mode]}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 font-mono text-zinc-600">
                          {a.client_id_masked || <em className="text-red-600">missing</em>}
                        </td>
                        <td className="py-2 pr-3">
                          {a.last_verified_status === "ok" && (
                            <span className="inline-flex items-center gap-1 text-emerald-700">
                              <CheckCircle2 className="h-3 w-3" />
                              {fmtDate(a.last_verified_at)}
                            </span>
                          )}
                          {a.last_verified_status === "error" && (
                            <span className="inline-flex items-center gap-1 text-red-700">
                              <XCircle className="h-3 w-3" />
                              {fmtDate(a.last_verified_at)}
                            </span>
                          )}
                          {!a.last_verified_status && (
                            <span className="text-zinc-400">— never —</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right">
                          <div className="inline-flex gap-1"
                               onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="outline"
                                    onClick={() => testAccount(a.account_id)}
                                    disabled={testingId === a.account_id}
                                    className="h-6 px-2 text-[10px] rounded-sm"
                                    data-testid={`paypal-test-${a.account_id}`}>
                              {testingId === a.account_id
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : <><ShieldCheck className="h-3 w-3 mr-1" /> Test</>}
                            </Button>
                            <Button size="sm" variant="outline"
                                    onClick={() => startEdit(a)}
                                    className="h-6 px-2 text-[10px] rounded-sm"
                                    data-testid={`paypal-edit-${a.account_id}`}>
                              Edit
                            </Button>
                            <Button size="sm" variant="ghost"
                                    onClick={() => deleteAccount(a.account_id)}
                                    className="h-6 px-2 text-red-600"
                                    data-testid={`paypal-delete-${a.account_id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / edit form */}
      <Card className="rounded-sm">
        <CardContent className="p-4 space-y-3">
          <div>
            <div className="label-overline">
              {form.account_id ? "Edit account" : "Add PayPal account"}
            </div>
            <div className="font-display font-bold text-lg mt-0.5">
              {form.account_id ? form.name || "…" : "New connection"}
            </div>
            {form.account_id && (
              <p className="text-[11px] text-zinc-500 mt-1">
                Leave Client ID / Secret blank to keep the existing values.
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Account name</Label>
              <Input value={form.name} className="mt-1"
                     onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                     placeholder="e.g. My Personal PayPal or Image Traders"
                     data-testid="paypal-form-name" />
            </div>
            <div>
              <Label className="text-[11px]">Environment</Label>
              <select value={form.mode}
                      onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}
                      className="w-full mt-1 rounded-sm border border-zinc-300 px-2 py-2 text-xs"
                      data-testid="paypal-form-mode">
                <option value="sandbox">Sandbox (safe testing)</option>
                <option value="live">Live (production)</option>
              </select>
            </div>
            <div>
              <Label className="text-[11px]">Client ID</Label>
              <Input value={form.client_id} className="mt-1 font-mono"
                     onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))}
                     placeholder="AeA1QIZXiflr1_-r0U…"
                     data-testid="paypal-form-client-id" />
            </div>
            <div>
              <Label className="text-[11px]">Client Secret</Label>
              <div className="relative mt-1">
                <Input value={form.client_secret}
                       type={showSecret ? "text" : "password"}
                       className="font-mono pr-9"
                       onChange={(e) => setForm((f) => ({ ...f, client_secret: e.target.value }))}
                       placeholder="EL1tT1n9…"
                       data-testid="paypal-form-client-secret" />
                <button type="button"
                        className="absolute inset-y-0 right-0 px-2 text-zinc-500 hover:text-zinc-900"
                        onClick={() => setShowSecret((v) => !v)}
                        aria-label="toggle secret visibility">
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px]">Notes (optional)</Label>
              <Textarea rows={2} value={form.notes} className="mt-1"
                        onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                        placeholder="e.g. Business account used for Image Traders retail sales."
                        data-testid="paypal-form-notes" />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={saveAccount} disabled={saving}
                    className="bg-[#002FA7] hover:bg-[#001F7A] rounded-sm"
                    data-testid="paypal-form-save">
              {saving
                ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</>
                : <><Save className="h-4 w-4 mr-1.5" /> {form.account_id ? "Update" : "Add account"}</>}
            </Button>
            {form.account_id && (
              <Button variant="outline" onClick={resetForm} className="rounded-sm">
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Selected account — balance + transactions */}
      {selected && (
        <>
          <Card className="rounded-sm" data-testid="paypal-balance-card">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-overline">Wallet balance</div>
                  <div className="font-display font-bold text-lg mt-0.5">
                    {selected.name}{" "}
                    <span className="text-xs font-normal text-zinc-500 ml-1">
                      {MODE_LABEL[selected.mode]}
                    </span>
                  </div>
                </div>
                <Button onClick={fetchBalance} disabled={balanceBusy}
                        className="bg-[#002FA7] hover:bg-[#001F7A] rounded-sm"
                        data-testid="paypal-load-balance">
                  {balanceBusy
                    ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Loading…</>
                    : <><RefreshCw className="h-4 w-4 mr-1.5" /> Load balance</>}
                </Button>
              </div>

              {balance ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                        <th className="py-2 pr-3">Currency</th>
                        <th className="py-2 pr-3">Available</th>
                        <th className="py-2 pr-3">Total</th>
                        <th className="py-2 pr-3">On hold</th>
                        <th className="py-2 pr-3">Primary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(balance.balances || []).map((b) => (
                        <tr key={b.currency} className="border-b border-zinc-100">
                          <td className="py-2 pr-3 font-mono font-bold">{b.currency}</td>
                          <td className="py-2 pr-3 font-bold text-emerald-700">
                            {fmtMoney(b.available_balance)}
                          </td>
                          <td className="py-2 pr-3">{fmtMoney(b.total_balance)}</td>
                          <td className="py-2 pr-3 text-amber-700">
                            {fmtMoney(b.withheld_balance)}
                          </td>
                          <td className="py-2 pr-3">
                            {b.primary && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="text-[10px] text-zinc-500 mt-2">
                    As of {fmtDate(balance.as_of_time)} · Account {balance.account_id}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-zinc-500">
                  Click <b>Load balance</b> to fetch the current wallet total.
                  PayPal balance updates can lag by up to 3 hours.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-sm" data-testid="paypal-transactions-card">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-overline">Recent transactions</div>
                  <div className="font-display font-bold text-lg mt-0.5">
                    Last 30 days
                  </div>
                </div>
                <Button onClick={() => fetchTransactions(1)} disabled={txBusy}
                        className="bg-[#002FA7] hover:bg-[#001F7A] rounded-sm"
                        data-testid="paypal-load-tx">
                  {txBusy
                    ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Loading…</>
                    : <><RefreshCw className="h-4 w-4 mr-1.5" /> Load transactions</>}
                </Button>
              </div>

              {tx ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                          <th className="py-2 pr-3">Date</th>
                          <th className="py-2 pr-3">Transaction ID</th>
                          <th className="py-2 pr-3">Amount</th>
                          <th className="py-2 pr-3">Fee</th>
                          <th className="py-2 pr-3">Status</th>
                          <th className="py-2 pr-3">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(tx.transaction_details || []).length === 0 && (
                          <tr>
                            <td colSpan={6} className="text-center text-zinc-500 py-4">
                              No transactions in this range.
                            </td>
                          </tr>
                        )}
                        {(tx.transaction_details || []).map((d, i) => {
                          const t = d.transaction_info || {};
                          const amt = Number(t.transaction_amount?.value || 0);
                          const dir = amt >= 0 ? "in" : "out";
                          return (
                            <tr key={`${t.transaction_id}-${i}`}
                                className="border-b border-zinc-100 hover:bg-zinc-50">
                              <td className="py-2 pr-3 whitespace-nowrap">
                                {fmtDate(t.transaction_initiation_date)}
                              </td>
                              <td className="py-2 pr-3 font-mono text-[11px]">
                                {t.transaction_id || "—"}
                              </td>
                              <td className={`py-2 pr-3 font-bold ${dir === "in" ? "text-emerald-700" : "text-red-700"}`}>
                                <span className="inline-flex items-center gap-1">
                                  {dir === "in"
                                    ? <ArrowDownCircle className="h-3 w-3" />
                                    : <ArrowUpCircle className="h-3 w-3" />}
                                  {fmtMoney(t.transaction_amount)}
                                </span>
                              </td>
                              <td className="py-2 pr-3 text-zinc-600">
                                {t.fee_amount ? fmtMoney(t.fee_amount) : "—"}
                              </td>
                              <td className="py-2 pr-3">
                                {TX_STATUS[t.transaction_status] || t.transaction_status || "—"}
                              </td>
                              <td className="py-2 pr-3 font-mono text-[10px] text-zinc-500">
                                {t.transaction_event_code || "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-between text-[11px] text-zinc-500">
                    <div>
                      Page {tx.page || txPage} of {tx.total_pages || 1}
                      {" · "}{tx.total_items ?? (tx.transaction_details || []).length} rows
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="rounded-sm h-7 text-[11px]"
                              disabled={txPage <= 1 || txBusy}
                              onClick={() => fetchTransactions(txPage - 1)}>
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-sm h-7 text-[11px]"
                              disabled={txBusy || (tx.total_pages && txPage >= tx.total_pages)}
                              onClick={() => fetchTransactions(txPage + 1)}>
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-xs text-zinc-500">
                  Click <b>Load transactions</b> to pull the last 30 days of
                  balance-affecting activity. PayPal caps each request at 31
                  days / 10,000 rows and new transactions can take up to 3
                  hours to appear.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Footer help */}
      <div className="text-[10px] text-zinc-400 flex items-center gap-2">
        <Wallet className="h-3 w-3" />
        Powered by PayPal REST v1 · secrets encrypted at rest · sandbox and
        live are per-account so you can promote to Live one account at a time.
      </div>
    </div>
  );
}
